import { createRouter, createWebHistory } from 'vue-router'

import { speckleCallbackRoute } from '@haskoning/vue-speckle'

import ProjectIndexView from '@/views/ProjectIndexView.vue'
import ProjectDetailView from '@/views/ProjectDetailView.vue'
import UserManagementView from '@/views/UserManagementView.vue'
import BoundaryConditionsView from '@/views/BoundaryConditionsView.vue'
import SoilTypesView from '@/views/SoilTypesView.vue'
import TestView from '@/views/TestView.vue'
import StageConfigurationView from '@/views/StageConfigurationView.vue'
import StabSchematisationView from '@/views/SchematisationDetailView.vue'
import YearsView from '@/views/YearsView.vue'
import WaterLineRuleView from '@/views/WaterLineRuleView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import { REVISION_KEY } from '@/components/constant'
import BurstingParameterView from '@/views/BurstingParameterView.vue'

export const routes = [
  {
    path: '/test',
    name: 'test-view',
    meta: {
      title: 'Test',
      hideSideBar: true
    },
    get component() {
      return TestView
    }
  },
  {
    path: '/',
    name: 'project-index',
    meta: {
      title: 'Home',
      hideSideBar: true
    },
    get component() {
      return ProjectIndexView
    }
  },
  {
    path: '/project/:id/',
    name: 'project-detail',
    meta: {
      title: 'Project'
    },
    get component() {
      return ProjectDetailView
    },
    children: [
      {
        path: '/project/:id/user-management',
        name: 'user-management',
        get component() {
          return UserManagementView
        },
        meta: {
          title: 'User management',
          sideBar: true,
          icon: 'mdi-account-group',
          model: 'user'
        }
      },
      {
        path: '',
        name: 'dikeIndex-trajectory',
        component: () => import('@/views/DikeTrajectoryView.vue'),
        meta: {
          title: 'Dike trajectory',
          sideBar: true,
          icon: 'mdi-map',
          model: 'dikesection'
        }
      },
      {
        path: '/project/:id/boundary-conditions',
        name: 'boundary-conditions',
        get component() {
          return BoundaryConditionsView
        },
        meta: {
          title: 'Boundary conditions',
          sideBar: true,
          icon: 'mdi-waves',
          tab: {
            'river-water-level': { title: 'River water level', model: 'riverwaterlevel' },
            settlement: { title: 'Settlement', model: 'settlement' },
            'crest-level': { title: 'Crest level', model: 'crestlevelboundarycondition' },
            'hydraulic-head': { title: 'Hydraulic head', model: 'hydraulicheadboundarycondition' }
          }
        }
      },

      {
        path: '/project/:id/years',
        get component() {
          return YearsView
        },
        meta: {
          title: 'Design years',
          sideBar: true,
          select: true,
          icon: 'mdi-calendar-multiple'
        }
      },

      {
        path: '/project/:id/water-line-rule/revision/:revisionId',
        name: 'water-line-rules',
        get component() {
          return WaterLineRuleView
        },
        meta: {
          title: 'Water line rules',
          sideBar: true,
          icon: 'mdi-window-shutter-cog',
          model: 'waterlinerule',
          key: 'selected_water_line_rule_revision'
        }
      },
      {
        path: '/project/:id/soil-types/revision/:revisionId',
        name: 'soil-types',
        get component() {
          return SoilTypesView
        },
        meta: {
          title: 'Soil types',
          sideBar: true,
          icon: 'mdi-landslide',
          subItem: true,
          key: REVISION_KEY.SOIL_TYPE,
          tab: {
            'soil-type': { title: 'Soil type', model: 'soil-type' },
            'su-table': { title: 'Su table', model: 'sutable' }
          }
        }
      },
      {
        path: '/project/:id/stage-configuration/revision/:revisionId',
        name: 'stage-configuration',
        get component() {
          return StageConfigurationView
        },
        meta: {
          title: 'Stage configuration',
          sideBar: true,
          icon: 'mdi-table',
          subItem: true,
          model: 'stageconfiguration',
          key: REVISION_KEY.STAGE_CONFIGURATION
        }
      },
      {
        path: '/project/:id/bursting-parameter/revision/:revisionId',
        name: 'bursting-parameter',
        get component() {
          return BurstingParameterView
        },
        meta: {
          title: 'Bursting parameter',
          sideBar: true,
          icon: 'mdi-pipe-leak',
          subItem: true,
          model: 'burstingparameter',
          key: REVISION_KEY.BURSTING_PARAMETER
        }
      },
      {
        path: '/project/:id/revision/:revisionId/schematisation/:schematisationId',
        name: 'schematisation',
        get component() {
          return StabSchematisationView
        },
        children: [
          {
            path: '/project/:id/revision/:revisionId/schematisation/:schematisationId/schematisation-stage/:schematisationStageId',
            name: 'schematisation-stage',
            get component() {
              return StabSchematisationView
            },
            meta: {
              title: 'Stab schematisation view',
              sideBarRight: true,
              tab: {
                'topo-profile': { title: 'Topo profile', model: 'topoprofile' },
                'phreatic-line': { title: 'Phreatic line', model: 'phreaticline' },
                'head-line': { title: 'Head line', model: 'headline' },
                'soil-profile-block': { title: 'Soil profile', model: 'soilprofile' },
                'soil-profile-wedge': { title: 'Soil profile', model: 'soilprofile' },
                reinforcement: { title: 'Reinforcement', model: 'reinforcement' },
                'state-point': { title: 'State point', model: 'statepoint' },
                load: { title: 'Load', model: 'uniformload' },
                overview: { title: 'Overview', model: 'stabschematisationstage' }
              }
            }
          }
        ],
        meta: {
          title: 'Stab schematisation view',
          sideBarRight: true
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found-view',
    meta: {
      title: '404 Not Found',
      hideSideBar: true
    },
    get component() {
      return NotFoundView
    }
  },
  speckleCallbackRoute
]

const router = createRouter({
  mode: 'history',
  history: createWebHistory(import.meta.env.BASE_URL),
  base: import.meta.env.BASE_URL,
  routes
})

export default router
